<template>
	<div class="stepper-wrapper" v-if="hasAccess">
		<template v-if="!formIsSubmitted">
			<div class="stepper-header" style="min-height:100px;">
				<div class="steps" style="margin-top: -25px;">
					<template v-for="( step, index ) in formData.PAGES">
						<div :class="['step', isStepActive(step) ? 'active' : 'inactive', isStepCompleted ? 'completed' : '']"
								:key="index" :style="{ width: `${100 / formData.PAGES.length}%` }">
							<div class="divider-line"></div>
							<div class="circle" @click="onClickStep( step, index )">
								<strong>
									{{ $t('MAIN_CONTENT.DataForm.stepNumber', { str: `${+index + 1}`}) }}
								</strong>
							</div>
							<div class="step-title">
								<strong>{{ step.PAGE_DESC }}</strong>
							</div>
						</div>
					</template>
				</div>
			</div>

			<div class="stepper-content"
					v-for="(step, stepIndex) in formData.PAGES"
					:key="`form-fields_${stepIndex}`">
				<template v-if="step === currentStep">
					<div class="panel panel-plain">
						<div class="panel-heading clearfix">
							<h2 class="panel-title">
								{{ $t('MAIN_CONTENT.DataForm.test', { description: step.PAGE_DESC } ) }}
								<i class="fa fa-info-circle" v-if="!!step.DISTRICT_HELP_TEXT" v-tooltip.top-center="{content: `${step.DISTRICT_HELP_TEXT}`, trigger: 'hover click'}" />
								<span class="pull-right">
									{{ $t('MAIN_CONTENT.DataForm.currentStepOfTotal', { current: +stepIndex + 1, total: formData.PAGES.length }) }}
								</span>
							</h2>
						</div>
						<div class="row" style="margin-left: 0; margin-right: 0; margin-top: 15px;" v-if="isReadOnly">
							<div class="col-xs-8 col-md-offset-2 text-center">
								<div :class="{'col-xs-12 alert':true, 'alert-success': formData.FORM_STATUS_FLG === 'A', 'alert-danger': formData.FORM_STATUS_FLG === 'D', 'alert-info': formData.FORM_STATUS_FLG === 'P'}">
									
									<p v-if="formData.FORM_STATUS_FLG === 'A'">{{ $t('MAIN_CONTENT.StudentDataForms.approvedMessageText') }}</p>
									<p v-else-if="formData.FORM_STATUS_FLG === 'D'">{{ $t('MAIN_CONTENT.StudentDataForms.declinedMessageText') }}<br/>
										{{formData.DENY_REASON_TEXT}}
									</p>
									<p v-else-if="formData.FORM_STATUS_FLG === 'P'">{{ $t('MAIN_CONTENT.StudentDataForms.pendingMessageText') }}</p>
									<p v-else>{{ $t('MAIN_CONTENT.StudentDataForms.readonlyMessageText') }}</p>
									<div class="col-xs-8 col-md-offset-2 text-center">
										<br/>
										<a href="#" @click.prevent="closeForm()" class="btn btn-primary btn-lg btn-block">
											{{ $t('MAIN_CONTENT.commonContent.closeButton') }}
										</a>
									</div>
								</div>
								
							</div>
						</div>
						<template v-if="Object.keys(message).length > 0">
							<br />
							<div class="row" style="margin-left: 0; margin-right: 0;">
								<div :class="{'col-xs-12 alert':true, 'alert-success': message.TYPE === 'success', 'alert-danger': message.TYPE === 'danger'}">
									{{ message.TEXT }}
								</div>
							</div>
						</template>

						<!-- Start of Steps details -->
						<div class="panel-body" v-for="(group, groupIndex) in step.GROUPS" :key="`group_${groupIndex}`">
							<!-- Start check if group shows at all based upon fields -->
							<template v-if="displayGroup(group)">
								<div class="row">
									<div class="col-xs-12">
										<h4>{{ group.GROUP_DESC }}
											<i class="fa fa-info-circle" v-if="!!group.DISTRICT_HELP_TEXT" v-tooltip.top-center="{content: `${group.DISTRICT_HELP_TEXT}`, trigger: 'hover click'}" />
										</h4>
									</div>
								</div>
								<div class="row">
									<!-- Start of Fields in each group -->
									<div class="col-xs-12"
											:style="getFieldStyle(field)"
											v-for="(field, fieldIndex) in group.FIELDS" :key="`field_${fieldIndex}`">
										<!-- Label that is used on every form field -->
										<label :for="`step_${stepIndex}_group_${groupIndex}_field_${fieldIndex}`"
													:class="{'normal-font': field.FIELD_INPUT === 'HT'}"
													
													v-if="field.FIELD_INPUT !== 'EM' && !isMedWidget(field) && field.WIDGET_CODE !== 'SIS_MEDICAL' && field.WIDGET_CODE !== 'SIS_ATTEND'">
											<template v-if="field.FIELD_INPUT !== 'HT'">
												{{ field.FIELD_LABEL }} <i class="fa fa-info-circle" v-if="!!field.DISTRICT_HELP_TEXT" v-tooltip.top-center="{content: getTooltip(field), trigger: 'hover click'}" />
											</template>
											<template v-else>
												<span v-html="field.DISTRICT_HELP_TEXT"></span>
											</template>
											<template v-if="(field.FIELD_DISTRICT_REQ === 'Y' && !( field.FIELD_INPUT === 'LT' || field.FIELD_INPUT === 'HT' || field.FIELD_INPUT === 'EM'))">
												<span style="color: red;">*</span>
											</template>
										</label>
										<template v-if="field.FIELD_FILE_URL && field.WIDGET_CODE !== 'SIS_ATTEND'">
											<div>
												{{ $t('MAIN_CONTENT.DataForm.attachmentWithColon') }}  <a :href="field.FIELD_FILE_URL" target="_blank">{{field.FIELD_DOC_FILENAME}}</a>
											</div>
										</template>
										<ValidationProvider :name="field.FIELD_LABEL"
																				:rules="(field.FIELD_DISTRICT_REQ === 'Y' && !( field.FIELD_INPUT === 'LT' || field.FIELD_INPUT === 'HT' || field.FIELD_INPUT === 'EM')) ? 'required' : ''">
											<div slot-scope="{ errors }">
												<!-- Input field is a number -->
												<template v-if="field.FIELD_TYPE === 'NUM'">
													<input type="text"
																class="form-control"
																:id="`step_${stepIndex}_group_${groupIndex}_field_${fieldIndex}`"
																:name="`step_${stepIndex}_group_${groupIndex}_field_${fieldIndex}`"
																:maxlength="field.FIELD_LENGTH"
																v-model="field.DISPLAY_VALUE"
																:disabled="isReadOnly">
												</template>
												<!-- Input field is a string -->
												<template v-if="field.FIELD_TYPE === 'STR' && field.FIELD_INPUT !== 'HT'">
													<template v-if="field.FIELD_INPUT === 'IT'">
														<input type="text"
																	class="form-control"
																	:id="`step_${stepIndex}_group_${groupIndex}_field_${fieldIndex}`"
																	:name="`step_${stepIndex}_group_${groupIndex}_field_${fieldIndex}`"
																	:maxlength="field.FIELD_LENGTH"
																	v-model="field.DISPLAY_VALUE"
																	:disabled="isReadOnly">
													</template>
													<!-- Input field is an iFrame -->
													<template v-if="field.FIELD_INPUT === 'EM'">
														<vue-friendly-iframe class="viewport"
																								:src="field.FIELD_EMBEDED_URL_LINK"
																								style="width: 100%">
														</vue-friendly-iframe>
													</template>
													<!-- Input field is a sinle list choice drop-down -->
													<template v-if="field.FIELD_INPUT === 'LS'">
														<select class="form-control"
																		:name="`step_${stepIndex}_group_${groupIndex}_field_${fieldIndex}`"
																		:id="`step_${stepIndex}_group_${groupIndex}_field_${fieldIndex}`"
																		v-model="field.DISPLAY_VALUE"
																		:disabled="isReadOnly">
															<template v-for="(code, codeIndex) in formCodes[field.FIELD_ID]">
																<option :value="code.LIST_VALUE" v-if="!code.hasOwnProperty('ACTIVE_FLG') || code.ACTIVE_FLG == 'Y'"
																				:key="`step_${stepIndex}_group_${groupIndex}_field_${fieldIndex}_${codeIndex}`">
																	{{ code.LIST_DESC }}
																</option>
															</template>
														</select>
													</template>
													<!-- Input field is a sinle list choice drop-down blank allowed -->
													<template v-if="field.FIELD_INPUT === 'LSB'">
														<select class="form-control"
																		:name="`step_${stepIndex}_group_${groupIndex}_field_${fieldIndex}`"
																		:id="`step_${stepIndex}_group_${groupIndex}_field_${fieldIndex}`"
																		v-model="field.DISPLAY_VALUE"
																		:disabled="isReadOnly">
															<option value=""></option>
															<template v-for="(code, codeIndex) in formCodes[field.FIELD_ID]">
																<option :value="code.LIST_VALUE" v-if="!code.hasOwnProperty('ACTIVE_FLG') || code.ACTIVE_FLG == 'Y'"
																				:key="`step_${stepIndex}_group_${groupIndex}_field_${fieldIndex}_${codeIndex}`">
																	{{ code.LIST_DESC }}
																</option>
															</template>
														</select>
													</template>
													<!-- Input field is a file upload -->
													<template v-if="field.FIELD_INPUT === 'FU'" >
														<div class="dropzone-area" v-if="!isReadOnly && loadedFilePickerData" @mousedown="doFetchToken">
															<file-uploader
																ref="fileUpload"
																:nameHeader="$t( 'MAIN_CONTENT.commonContent.name' )"
																:sizeHeader="$t( 'MAIN_CONTENT.commonContent.size' )"
																:removeHeader="$t( 'MAIN_CONTENT.commonContent.remove' )"
																:dropSingleFile="$t( 'MAIN_CONTENT.commonContent.dropSingleFile' )"
																:dropMultipleFiles="$t( 'MAIN_CONTENT.commonContent.dropMultipleFiles' )"
																:selectSingleFile="$t( 'MAIN_CONTENT.commonContent.selectSingleFile' )"
																:selectMultipleFiles="$t( 'MAIN_CONTENT.commonContent.selectMultipleFiles' )"
																:attachFile="$t( 'MAIN_CONTENT.commonContent.attachFile' )"
																:attachFiles="$t( 'MAIN_CONTENT.commonContent.attachFiles' )"
																:size="52428800"
																:inputId="`fileUpload_${field.FIELD_ID}`"
																:multiple="true"
																:files="tempFiles[field.FIELD_ID]"
																:URL="externalFileData"
																:accept="'application/*,image/*,audio/*,video/*,text/plain,text/richtext'"
																:postAction="`/PortalAPI/index.cfm/api/v1/data-verification-forms/field-files?dv_form_id=${field.DV_FORM_ID}&field_id=${field.FIELD_ID}&instance_id=${$route.params.instanceID}&student_rid=${selectedStudent.STUDENT_RID}`"
																
																:auth="{
																	'Authorization': $store.state.accessToken,
																	'Client-ID': $store.state.clientID
																}"
																@updateFiles="appendFile($event, field.FIELD_ID)"
																@saveGoogleFilesData="saveGoogleFileData(file=$event, multiple=true, field.FIELD_ID)"
																@saveDropboxFilesData="saveDropboxFileData(file=$event, multiple=true, field.FIELD_ID)"
																@saveOneDriveFilesData="saveOneDriveFileData(file=$event, multiple=true, field.FIELD_ID)"
															>
															</file-uploader>
														</div>
														<div class="row">
															<div class="col-xs-12">
																<p><strong>{{ $t('MAIN_CONTENT.DataForm.uploadFilesTitleCase') }}</strong></p>
															</div>
														</div>
														<ul class="list-group">
															<li class="list-group-item" v-for="(file, index) in formValues[field.FIELD_ID]" :key="file.HASHA + file.FILE_IDX">
																<div class="row">
																	<div class="col-xs-11">
																		<a :href="file.FILE_URL" target="_blank">{{file.DV_FILE_NAME}}</a>
																	</div>
																	<div class="col-xs-1 text-right">
																	<a href="#" 
																		@click.prevent="deleteFile(file.DV_FORM_ID, file.FIELD_ID, file.FILE_IDX, file.INSTANCE_ID, file.STUDENT_RID, index)" 
																	>
																		<span class="label label-danger"><font-awesome-icon icon="times" /></span>
																	</a>
																	</div>
																</div>
															</li>
														</ul>
													</template>
													<!-- Input field is a text area -->
													<template v-if="field.FIELD_INPUT === 'TAN' || field.FIELD_INPUT === 'TAW'">
														<textarea class="form-control"
																			:name="`step_${stepIndex}_group_${groupIndex}_field_${fieldIndex}`"
																			:id="`step_${stepIndex}_group_${groupIndex}_field_${fieldIndex}`"
																			:maxlength="field.FIELD_LENGTH"
																			v-model="field.DISPLAY_VALUE"
																			:disabled="isReadOnly">
														</textarea>
													</template>
													<!-- Multi-select list -->
													<template v-if="field.FIELD_INPUT === 'LM'">
														<template v-for="(code, codeIndex) in formCodes[field.FIELD_ID]">
															<span style="display: block;"
																		:key="`step_${stepIndex}_group_${groupIndex}_field_${fieldIndex}_${codeIndex}`">
																<input type="checkbox"
																			:name="`step_${stepIndex}_group_${groupIndex}_field_${fieldIndex}_code_${code.LIST_VALUE}`"
																			:id="`step_${stepIndex}_group_${groupIndex}_field_${fieldIndex}_code_${code.LIST_VALUE}`"
																			:value="code.LIST_VALUE"
																			v-model="field.DISPLAY_VALUE" 
																			:disabled="isReadOnly"/>
																<label :for="`step_${stepIndex}_group_${groupIndex}_field_${fieldIndex}_code_${code.LIST_VALUE}`">
																	{{ code.LIST_DESC }}
																</label>
															</span>
														</template>
													</template>
													<!-- Custom Widget -->
													<template v-if="field.FIELD_INPUT === 'WID'">
														<template v-if="field.WIDGET_CODE === 'CONTACTS'">
															<sis-student-contacts
																:helpText="field.DISTRICT_HELP_TEXT"
																:contacts="field.DISPLAY_VALUE.CONTACTS"
																:counties="formCodes.COUNTIES"
																:releaseStatus="formCodes.RELEASE_STATUS"
																:townships="formCodes.TOWNSHIPS"
																:states="formCodes.STATES"
																:showContactNameFlag="!!field.WIDGET_OPTIONS.WID_SHOW_NAME_FLG && field.WIDGET_OPTIONS.WID_SHOW_NAME_FLG === 'Y'"
																:showAddressFlag="!!field.WIDGET_OPTIONS.WID_SHOW_NAME_ADDRESS_FLG && field.WIDGET_OPTIONS.WID_SHOW_NAME_ADDRESS_FLG === 'Y'"
																:showEmailAddressFlag="!!field.WIDGET_OPTIONS.WID_SHOW_EMAIL_FLG && field.WIDGET_OPTIONS.WID_SHOW_EMAIL_FLG === 'Y'"
																:showLivesWithFlag="!!field.WIDGET_OPTIONS.WID_SHOW_NAME_LIVES_WITH_FLG && field.WIDGET_OPTIONS.WID_SHOW_NAME_LIVES_WITH_FLG === 'Y'"
																:showReleaseToFlag="!!field.WIDGET_OPTIONS.WID_SHOW_NAME_RELEASE_STATUS_FLG && field.WIDGET_OPTIONS.WID_SHOW_NAME_RELEASE_STATUS_FLG === 'Y'"
																:showPhoneNumbersFlag="!!field.WIDGET_OPTIONS.WID_SHOW_PHONE_FLG && field.WIDGET_OPTIONS.WID_SHOW_PHONE_FLG === 'Y'"
																:showEmployerOccupationFlag="!!field.WIDGET_OPTIONS.WID_SHOW_NAME_EMPLOYER_FLG && field.WIDGET_OPTIONS.WID_SHOW_NAME_EMPLOYER_FLG === 'Y'"
																:isReadOnly="isReadOnly"
																:titleText="$t( 'MAIN_CONTENT.DataForm.title' )"
																:middleNameText="$t( 'MAIN_CONTENT.DataForm.middleName' )"
																:contactLivesWithStudentText="$t( 'MAIN_CONTENT.DataForm.contactLivesWithStudent' )"
																:yesText="$t( 'MAIN_CONTENT.commonContent.yesLabel' )"
																:noText="$t( 'MAIN_CONTENT.commonContent.noLabel' )"
																:releaseStatusText="$t( 'MAIN_CONTENT.DataForm.releaseStatus' )"
																:addressOneText="$t( 'MAIN_CONTENT.DataForm.addressOne' )"
																:addressTwoText="$t( 'MAIN_CONTENT.DataForm.addressTwo' )"
																:cityText="$t( 'MAIN_CONTENT.DataForm.city' )"
																:stateText="$t( 'MAIN_CONTENT.DataForm.state' )"
																:extensionText="$t( 'MAIN_CONTENT.DataForm.ext' )"
																:typeText="$t( 'MAIN_CONTENT.DataForm.type' )"
																:cellPhoneText="$t( 'MAIN_CONTENT.DataForm.cellPhone' )"
																:faxNumberText="$t( 'MAIN_CONTENT.DataForm.faxNumber' )"
																:homePhoneText="$t( 'MAIN_CONTENT.DataForm.homePhone' )"
																:pageNumberText="$t( 'MAIN_CONTENT.DataForm.pageNumber' )"
																:workPhoneText="$t( 'MAIN_CONTENT.DataForm.workPhone' )"
																:unlistedText="$t( 'MAIN_CONTENT.DataForm.unlisted' )"
																:receivesVoiceCallsText="$t( 'MAIN_CONTENT.DataForm.receivesVoiceCalls' )"
																:emergencyCallOrder="$t( 'MAIN_CONTENT.DataForm.emergencyCallOrder' )"
																:emailText="$t( 'MAIN_CONTENT.DataForm.email' )"
																:occupationText="$t( 'MAIN_CONTENT.DataForm.occupation' )"
																:employerText="$t( 'MAIN_CONTENT.DataForm.employer' )"
																:zipCodeText="$t( 'MAIN_CONTENT.DataForm.zipCode' )"
																:zipCodeExtText="$t( 'MAIN_CONTENT.DataForm.zipCodeExt' )"
																:townshipText="$t( 'MAIN_CONTENT.DataForm.township' )"
																:countyText="$t( 'MAIN_CONTENT.DataForm.county' )"
																:numberText="$t( 'MAIN_CONTENT.DataForm.number' )"
															>
															</sis-student-contacts>
														</template>
														<template v-if="isMedWidget(field) && Object.keys(field.DISPLAY_VALUE[field.WIDGET_CODE]).length !== 0">
															<sn-medical-widget 
																:formData="field.DISPLAY_VALUE"
																:helpText="field.DISTRICT_HELP_TEXT"
																:title="field.FIELD_LABEL"
																:formType="field.WIDGET_CODE"
																:yesText="$t( 'MAIN_CONTENT.commonContent.yesLabel' )"
																:noText="$t( 'MAIN_CONTENT.commonContent.noLabel' )"
																:isReadOnly="isReadOnly"
															>
															</sn-medical-widget>
														</template>
														<template v-if="field.WIDGET_CODE === 'SIS_MEDICAL'">
															<sis-student-medical :formData="field.DISPLAY_VALUE"
																:hospitals="formCodes.HOSPITALS"
																:states="formCodes.STATES"
																:title="field.FIELD_LABEL"
																:helpText="field.DISTRICT_HELP_TEXT"
																:showInsuranceFlag="!!field.WIDGET_OPTIONS.WID_SHOW_INSURANCE && field.WIDGET_OPTIONS.WID_SHOW_INSURANCE === 'Y'"
																:showHospitalPreferenceFlag="!!field.WIDGET_OPTIONS.WID_HOSPITAL_PREFERENCE && field.WIDGET_OPTIONS.WID_HOSPITAL_PREFERENCE === 'Y'"
																:showPhysicianFlag="!!field.WIDGET_OPTIONS.WID_PHYSICIAN && field.WIDGET_OPTIONS.WID_PHYSICIAN === 'Y'"
																:showDentistFlag="!!field.WIDGET_OPTIONS.WID_DENTIST && field.WIDGET_OPTIONS.WID_DENTIST === 'Y'"
																:insuranceCompanyText="$t( 'MAIN_CONTENT.DataForm.insuranceCompany' )"
																:policyNumberText="$t( 'MAIN_CONTENT.DataForm.policyNumber' )"
																:idNumberText="$t( 'MAIN_CONTENT.DataForm.idNumber' )"
																:groupNumberText="$t( 'MAIN_CONTENT.DataForm.groupNumber' )"
																:hospitalPreferenceText="$t( 'MAIN_CONTENT.DataForm.hospitalPreference' )"
																:physicianNameText="$t( 'MAIN_CONTENT.DataForm.physicianName' )"
																:physicianPhoneText="$t( 'MAIN_CONTENT.DataForm.physicianPhone' )"
																:extText="$t( 'MAIN_CONTENT.DataForm.ext' )"
																:physicianFaxText="$t( 'MAIN_CONTENT.DataForm.physicianFax' )"
																:physicianFaxNumberText="$t( 'MAIN_CONTENT.DataForm.physicianFaxNumber' )"
																:addressOneText="$t( 'MAIN_CONTENT.DataForm.addressOne' )"
																:addressTwoText="$t( 'MAIN_CONTENT.DataForm.addressTwo' )"
																:stateText="$t( 'MAIN_CONTENT.DataForm.state' )"
																:cityText="$t( 'MAIN_CONTENT.DataForm.city' )"
																:zipCodeText="$t( 'MAIN_CONTENT.DataForm.zipCode' )"
																:zipCodeExtensionText="$t( 'MAIN_CONTENT.DataForm.zipCodeExtension' )"
																:phoneNumberExtensionText="$t( 'MAIN_CONTENT.DataForm.phoneNumberExtension' )"
																:physicianFaxNumberExtensionText="$t( 'MAIN_CONTENT.DataForm.physicianFaxExtension' )"
																:isReadOnly="isReadOnly"
															>
															</sis-student-medical>
														</template>
														<template v-if="field.WIDGET_CODE === 'SIS_ATTEND' && !!formDataInit.DISTRICT_ID">
															<sis-attendance-widget
																:formData="field.DISPLAY_VALUE.DATA"
																:field="field"
																:formValues="formValues[field.FIELD_ID]"
																:selectedStudent="selectedStudent"
																:accessToken="$store.state.accessToken"
																:clientID="$store.state.clientID"
																:instanceID="$route.params.instanceID"
																:externalFileData="externalFileData"
																@updateFiles2="appendFile"
																@saveGoogleFilesData2="saveGoogleFileData"
																@saveDropboxFilesData2="saveDropboxFileData"
																@saveOneDriveFilesData2="saveOneDriveFileData"
																@deleteFile2="deleteFile"
																@doFetchToken="doFetchToken"
																:loadedFilePickerData="loadedFilePickerData"
																:isReadOnly="isReadOnly"
																:disabledDates="[]"
																:updatedText="{
																	attachmentWithColon: $t( 'MAIN_CONTENT.DataForm.attachmentWithColon' ),
																	absenceDateFrom: $t( 'MAIN_CONTENT.DataForm.absenceDateFrom' ),
																	absenceDate: $t( 'MAIN_CONTENT.DataForm.absenceDate' ),
																	attendanceDateMessage: $t( 'MAIN_CONTENT.DataForm.attendanceDateMessage' ),
																	absenceReason: $t( 'MAIN_CONTENT.DataForm.absenceReason' ),
																	attachDocuments: $t( 'MAIN_CONTENT.DataForm.attachDocuments' ),
																	uploadFilesTitleCase: $t( 'MAIN_CONTENT.DataForm.uploadFilesTitleCase' ),
																	attendanceNoDatesMessage: $t( 'MAIN_CONTENT.DataForm.attendanceNoDatesMessage' ),
																	absenceDateTo: $t( 'MAIN_CONTENT.DataForm.absenceDateTo' ),
																	selectDates: $t( 'MAIN_CONTENT.DataForm.selectDates' )
																}"
															>
															</sis-attendance-widget>
																
														</template>
													</template>
												</template>
												<!-- Input Field is a Date -->
												<template v-if="field.FIELD_TYPE === 'DATE'">
													<date-picker :id="`date_${stepIndex}_group_${groupIndex}_field_${fieldIndex}`"
																			:name="`date_${stepIndex}_group_${groupIndex}_field_${fieldIndex}`"
																			lang="en"
																			style="display: contents;"
																			format="MM/DD/YYYY"
																			v-model="field.DISPLAY_VALUE"
																			:disabled="isReadOnly">
													</date-picker>
												</template>
												<!-- Input Field is an email address -->
												<template v-if="field.FIELD_TYPE === 'EMAIL'">
													<input type="email"
																class="form-control"
																:id="`email_${stepIndex}_group_${groupIndex}_field_${fieldIndex}`"
																:name="`email_${stepIndex}_group_${groupIndex}_field_${fieldIndex}`"
																:maxlength="field.FIELD_LENGTH"
																v-model="field.DISPLAY_VALUE" :disabled="isReadOnly">
												</template>
												<!-- Input Field is a zip -->
												<template v-if="field.FIELD_TYPE === 'ZIP'">
													<input :id="`zip_${stepIndex}_group_${groupIndex}_field_${fieldIndex}`"
																:name="`zip_${stepIndex}_group_${groupIndex}_field_${fieldIndex}`"
																:maxlength="field.FIELD_LENGTH"
																type="text"
																class="form-control"
																v-model="field.DISPLAY_VALUE" :disabled="isReadOnly">
												</template>
												<!-- Input field is a yes/no drop-down defaulting to yes -->
												<template v-if="field.FIELD_TYPE === 'Y/N'">
													<select class="form-control"
																	:name="`step_${stepIndex}_group_${groupIndex}_field_${fieldIndex}`"
																	:id="`step_${stepIndex}_group_${groupIndex}_field_${fieldIndex}`"
																	v-model="field.DISPLAY_VALUE" :disabled="isReadOnly">
														<option value="" v-if="field.FIELD_INPUT === 'LSB'"></option>
														<option value="Y">{{ $t('MAIN_CONTENT.commonContent.yesLabel') }}</option>
														<option value="N">{{ $t('MAIN_CONTENT.commonContent.noLabel') }}</option>
													</select>
												</template>
												<!-- Input field is a no/yes drop-down defaulting to no -->
												<template v-if="field.FIELD_TYPE === 'N/Y'">
													<select class="form-control"
																	:name="`step_${stepIndex}_group_${groupIndex}_field_${fieldIndex}`"
																	:id="`step_${stepIndex}_group_${groupIndex}_field_${fieldIndex}`"
																	v-model="field.DISPLAY_VALUE" :disabled="isReadOnly">
														<option value="" v-if="field.FIELD_INPUT === 'LSB'"></option>
														<option value="N">{{ $t('MAIN_CONTENT.commonContent.noLabel') }}</option>
														<option value="Y">{{ $t('MAIN_CONTENT.commonContent.yesLabel') }}</option>
													</select>
												</template>
												<small class="text-danger">{{ errors[0] }}</small>
											</div>
										</ValidationProvider>
										<pre style="height:200px;overflow:scroll;" v-if="debug">{{ JSON.stringify(field, null, 2) }}</pre>
									</div>
									<!-- End of Fields in each group -->
								</div>

								<!-- Horizontal line needed for each group -->
								<hr v-if="(groupIndex + 1) !== step.GROUPS.length && groupToShowAfter(step.GROUPS).includes(group.GROUP_ID)" />
								<!-- End of fields in each group -->
							</template>
							<!-- End check if group shows at all based upon fields -->
						</div>
						<!-- End of Steps details -->

						<div class="panel-footer container-fluid">
							<button class="btn btn-primary previous pull-left"
											v-if="stepIndex > 0"
											@click="backStep()">
								<span>
									{{ $t('MAIN_CONTENT.DataForm.back') }}
								</span>
							</button>
							<button class="btn btn-primary next pull-right"
											@click="nextStep()"
											:disabled="!isStepCompleted && !isReadOnly"
											v-if="+stepIndex + 1 < formData.PAGES.length">
								<span>
									{{ $t('MAIN_CONTENT.commonContent.next') }}
								</span>
							</button>
							<button class="btn btn-primary pull-right"
											style="margin-right: 15px;"
											@click="saveProgress"
											:disabled="!isStepCompleted">
								<span v-if="+stepIndex + 1 < formData.PAGES.length">
									{{ $t('MAIN_CONTENT.DataForm.saveCurrentProgressTitleCase') }}
								</span>
								<span v-else>
									{{ $t('MAIN_CONTENT.commonContent.submitButton') }}
								</span>
							</button>
						</div>
					</div>
				</template>
			</div>
		</template>
		<div class="finished-form" v-if="formIsSubmitted">
			<div class="col-xs-4 col-md-offset-4 text-center">
				<div>
					<font-awesome-icon icon="check-circle" size="10x" class="text-muted"></font-awesome-icon>
				</div>
				<h2>{{ $t('MAIN_CONTENT.DataForm.formSubmitted') }}</h2>
				<p>{{ $t('MAIN_CONTENT.DataForm.successfullySubmitted') }}</p>
				<p><b>{{formData.DV_FORM_NAME}}</b></p>
				<div>
					<a href="#" @click.prevent="closeForm()" class="btn btn-lg btn-primary">
						{{ $t('MAIN_CONTENT.commonContent.closeButton') }}
					</a>
				</div>
			</div>
		</div>
	</div>
	<div v-else>
		You do not have access to this form.
	</div>
</template>

<script>
	import StudentDataForms from '../api/StudentDataForms.js';

	import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
	import VueFriendlyIframe from 'vue-friendly-iframe';
	import { VTooltip, VPopover, VClosePopover } from 'v-tooltip';
	import { ValidationProvider } from 'vee-validate';

	// Shared components in vuecommons
	import DatePicker from 'COMMONS/shared/DatePicker.vue';
	import SISStudentContacts from 'COMMONS/Widgets/SISStudentContacts.vue';
	import SNMedicalWidget from 'COMMONS/Widgets/SNMedicalWidget.vue';
	import SISStudentMedical from 'COMMONS/Widgets/SISStudentMedical.vue';
	import SISAttendanceWidget from 'COMMONS/Widgets/SISAttendanceWidget.vue';
	import ExternalFilePicker from '../api/ExternalFilePicker';
	import ExternalFilesPost from '../api/ExternalFilesPost';
	import FileUploader from 'COMMONS/shared/FileUploader.vue';
	import Modal from 'COMMONS/shared/Modal/Modal.vue';

	import { mapGetters, mapActions } from 'vuex';
	import moment from 'moment';

	export default {
		name: 'DataForm',
		data() {
			return {
				debug: false,
				currentStep:	{},
				attendDate:"",
				tempFiles:	{},
				formData:			{},
				formDataInit:   {},
				formCodes:		[],
				message:			{},
				formValues:		{},
				canContinue:	false,
				formIsSubmitted: false,
				loadedFilePickerData: false,
				externalFileData: {
					API_ORIGIN_URL:							'',
					DISTRICT_ID:								'',
					DROPBOX_API_CLIENT_ID:			'',
					DROPBOX_API_CLIENT_SECRET:	'',
					GOOGLE_API_APP_ID:					'',
					GOOGLE_API_CLIENT_ID:				'',
					GOOGLE_API_CLIENT_SECRET:		'',
					ONEDRIVE_API_CLIENT_ID:			'',
					ONEDRIVE_API_CLIENT_SECRET:	''
				},
				response:					'',
				oauthToken:				null,
				pickerApiLoaded:	false,
				appId:						"1012757059728",
				clientId:					"1012757059728-t729asauc8sij0h49470do16vhvf3v81.apps.googleusercontent.com",
				developerKey:			"AIzaSyD0heYF6QV5WnhtyVZ25_CI53tuGIZekXQ",
				scope:						[
					"https://www.googleapis.com/auth/drive.file", "https://www.googleapis.com/auth/drive.metadata"
				]
			};
		},
		computed: {
			...mapGetters(['selectedStudent', 'roleRules', 'accessToken', 'role', 'userIsAdmin']),
			hasAccess(){
				return (this.role === "parent" && this.formData.PARENT_ACCESS_FLG === 'Y') || (this.role === "student" && this.formData.STUDENT_ACCESS_FLG === 'Y');
			},
			isDirty(){
				return JSON.stringify(this.formDataInit) !== JSON.stringify(this.formData);
			},
			isReadOnly(){
				/* If admin and form is set to admin access is N then readonly mode */
				if(this.userIsAdmin && !!this.formData.ADMIN_ACCESS_FLG && this.formData.ADMIN_ACCESS_FLG == "N"){
					return true;
				}
				const status = ['P','A','D'];

				// checks whether an element is completed 
				const completed = (element) => element === this.formData.FORM_STATUS_FLG;
				return status.some(completed);
			},
			isStepCompleted() {
				if(this.isReadOnly){
					return false;
				}

				let completed = true;
				
				for(let g in this.currentStep.GROUPS) {
					let group = this.currentStep.GROUPS[g];

					for(let f in group.FIELDS) {
						let field = group.FIELDS[f];
		
						/* if file upload in progress stop page progression */
						if(this.tempFiles[field.FIELD_ID].length > 0){
							completed = false;
							return completed;
						}

						if( this.displayGroup(group) && field.FIELD_INPUT === 'FU' && field.FIELD_DISTRICT_REQ === 'Y' && field.DISPLAY_VALUE.length === 0 ){
							completed = false
						}

						if( this.displayGroup(group) && (field.FIELD_DISTRICT_REQ === 'Y' && !( field.FIELD_INPUT === 'LT' || field.FIELD_INPUT === 'HT' || field.FIELD_INPUT === 'EM')) && field.DISPLAY_VALUE.toString().trim().length === 0) {
							completed = false;
						}

						if(
							this.displayGroup(group) && 
							(
								field.WIDGET_CODE === 'SIS_ATTEND' && 
								!!field.DISPLAY_VALUE.DATA
							)
						){ 
							/* if existing submittion form */
							if(
								!!this.$route.params && 
								!!this.$route.params.formAction && 
								!!this.$route.params.attendanceDate
							){
								if(
									field.DISPLAY_VALUE.DATA.CHECKED_DATES.length === 0 ||
									field.DISPLAY_VALUE.DATA.ATTENDANCE_REASON.trim().length === 0 ||
									(
										!!field.WIDGET_OPTIONS.WID_REQUIRE_FILEUPLOAD_FLG && 
										field.WIDGET_OPTIONS.WID_REQUIRE_FILEUPLOAD_FLG === 'Y' && 
										field.DISPLAY_VALUE.DATA.FILES.length === 0
									)
								){
									completed = false;
								}
							} else if(
								/* if range form */
								field.DISPLAY_VALUE.DATA.WID_DATE_SETTING === "RANGE" && (
									(
										field.DISPLAY_VALUE.DATA.DATE_FROM.length === 0 || 
										field.DISPLAY_VALUE.DATA.DATE_TO.length === 0 || 
										field.DISPLAY_VALUE.DATA.ATTENDANCE_REASON.trim().length === 0 || 
										(
											!!field.WIDGET_OPTIONS.WID_REQUIRE_FILEUPLOAD_FLG && 
											field.WIDGET_OPTIONS.WID_REQUIRE_FILEUPLOAD_FLG === 'Y' && 
											field.DISPLAY_VALUE.DATA.FILES.length === 0
										) ||
										!(moment( field.DISPLAY_VALUE.DATE_FROM )._d instanceof Date) || 
										!(moment( field.DISPLAY_VALUE.DATE_TO )._d instanceof Date)
									) ||
									(
										(
											!field.WIDGET_OPTIONS.WID_ALLOW_HISTORIC_DATES_FLG || 
											field.WIDGET_OPTIONS.WID_ALLOW_HISTORIC_DATES_FLG !== "Y"
										) && 
										(	
											moment( field.DISPLAY_VALUE.DATA.DATE_FROM ).startOf('day') < moment().startOf('day')
											||
											moment( field.DISPLAY_VALUE.DATA.DATE_TO ).startOf('day') < moment().startOf('day')
										)
									)
								)
							){
								completed = false;
							} else if (
								/* else single date */
								field.DISPLAY_VALUE.DATA.DATE.length === 0 || 
								field.DISPLAY_VALUE.DATA.ATTENDANCE_REASON.trim().length === 0 ||
								(
									!!field.WIDGET_OPTIONS.WID_REQUIRE_FILEUPLOAD_FLG && 
									field.WIDGET_OPTIONS.WID_REQUIRE_FILEUPLOAD_FLG === 'Y' && 
									field.DISPLAY_VALUE.DATA.FILES.length === 0 
								) || 
								!(moment( field.DISPLAY_VALUE.DATA.DATE )._d instanceof Date) || 
								(
									(
										!field.WIDGET_OPTIONS.WID_ALLOW_HISTORIC_DATES_FLG || 
										field.WIDGET_OPTIONS.WID_ALLOW_HISTORIC_DATES_FLG !== "Y"
									) && 
									( 
										moment( field.DISPLAY_VALUE.DATA.DATE ).startOf('day') < moment().startOf('day')
									) 
								) 
							) {
								completed = false;
							}
							
						}
					}
				}

				return completed;
			},
		},
		components: {
			FontAwesomeIcon,
			DatePicker,
			'sis-student-contacts': SISStudentContacts,
			'sis-student-medical': SISStudentMedical,
			'sn-medical-widget': SNMedicalWidget,
			'sis-attendance-widget': SISAttendanceWidget,
			ValidationProvider,
			VueFriendlyIframe,
			ExternalFilePicker,
			ExternalFilesPost,
			FileUploader,
			Modal
		},
		directives: {
			'tooltip': VTooltip,
		},
		methods: {
			...mapActions(['fetchToken']),
			doFetchToken(){
				this.fetchToken();
			},
			getExternalData() {
				const self = this;

				const external = new ExternalFilePicker({
					query: {
						student_rid: self.selectedStudent.STUDENT_RID
					}
				});

				external.get().then(response => {
					self.$set(self, 'externalFileData', external.getResponseData());
					self.loadedFilePickerData = true;
				});
			},
			handleExternalFiles(data, fieldId){
				let forms = new StudentDataForms({
					routeParams: { setting: 'field-files' },
					query: {
						student_rid:	this.selectedStudent.STUDENT_RID,
						dv_form_id:		this.$route.params.formID,
						instance_id:	!!this.$route.params.instanceID ? this.$route.params.instanceID : 0,
						field_id: 		fieldId,
						action:			"attachExistingFile"
					},
					data: data
				});

				const promise = forms.post();

				promise.then(response => {
					if(this.formValues[fieldId].hasOwnProperty("DATA") && this.formValues[fieldId].DATA.hasOwnProperty('FILES')){
						this.formValues[fieldId].DATA.FILES.push({...itm.response.data});
					}
					else{
						this.formValues[fieldId].push(response.data.data);
					}
				});
			},
			saveGoogleFileData(file, multiple=true, fieldId) {
				const self = this;

				const external = new ExternalFilesPost({
					query: {
						student_rid: self.selectedStudent.STUDENT_RID,
						source: 'googleDrive',
						fielId: file.id
					},
					routeParams: {
						course_section_guid: self.$route.params.courseSectionGUID
					},
					data: {
						file_id: file.id,
						authorization: 'Bearer ' + file.authToken,
						file_name: file.name,
						file_mime: file.mimeType,
						web_content_link: file.webContentLink,
						web_view_link: file.webViewLink,
						storage: "sis"
					}
				});

				external.post().then(response => {
					let data = external.getResponseData();
					this.handleExternalFiles(data,fieldId)
				});
			},
			saveDropboxFileData(file, multiple=true, fieldId) {
				const self = this;

				const external = new ExternalFilesPost({
					query: {
						student_rid: self.selectedStudent.STUDENT_RID,
						source: 'dropbox',
						fielId: file.id
					},
					routeParams: {
						course_section_guid: self.$route.params.courseSectionGUID
					},
					data: JSON.stringify({
						authorization: file.authToken,
						file_name: file.name,
						web_content_link: file.link,
						storage: "sis"
					})
				});

				external.post().then(response => {
					let data = external.getResponseData();
					this.handleExternalFiles(data,fieldId);
				});
			},
			saveOneDriveFileData(file, multiple=true, fieldId) {
				const self = this;

				const external = new ExternalFilesPost({
					query: {
						student_rid: self.selectedStudent.STUDENT_RID,
						source: 'oneDrive',
						fielId: file.id
					},
					routeParams: {
						course_section_guid: self.$route.params.courseSectionGUID
					},
					data: JSON.stringify({
						file_name: file.name,
						web_content_link: file['@content.downloadUrl'],
						storage: "sis"
					})
				});

				external.post().then(response => {
					let data = external.getResponseData();
					this.handleExternalFiles(data,fieldId);
				});
			},
			getFieldStyle(field){
				const MedicalWidgetIsEmpty = this.isMedWidget(field) && Object.keys(field.DISPLAY_VALUE[field.WIDGET_CODE]).length === 0;
				return MedicalWidgetIsEmpty ? {} : { 'min-height': '58px', 'margin-bottom': '5px' };
			},
			loadFormData() {
				const self = this;

				let forms = new StudentDataForms({
					routeParams: { setting: 'form' },
					query: {
						student_rid:	self.selectedStudent.STUDENT_RID,
						dv_form_id:		self.$route.params.formID,
						instance_id:	!!self.$route.params.instanceID ? self.$route.params.instanceID : 0,
						form_status_flg: !!self.$route.query.formStatus ? self.$route.query.formStatus : "N",
					}
				});

				const promise = forms.get();

				promise.then(response => {
					self.$set(self, 'formData', forms.getResponseData());
					
					// set the step to the first page when first loading
					if(!!self.formData.PAGES && Object.keys(self.currentStep).length === 0) {
						self.currentStep = self.formData.PAGES[0];
					}

					for(let p in self.formData.PAGES) {
						let page = self.formData.PAGES[p];

						for(let g in page.GROUPS) {
							let group = page.GROUPS[g];

							for(let f in group.FIELDS) {
								let field = group.FIELDS[f];
								let val = '';
								self.$set(this.tempFiles, field.FIELD_ID, []);
								if(field.FIELD_INPUT === 'LM') {
									val = [];
								}

								self.$set(field, 'DISPLAY_VALUE', val);
							}
						}
					}

					self.loadFormValues();
					self.setPageTitle();
					
				});
			},
			loadFormCodes() {
				const self = this;

				let forms = new StudentDataForms({
					routeParams: { setting: 'form-codes' },
					query: {
						student_rid:	self.selectedStudent.STUDENT_RID,
						dv_form_id:		self.$route.params.formID,
						instance_id:	!!self.$route.params.instanceID ? self.$route.params.instanceID : 0,
						form_status_flg: !!self.$route.query.formStatus ? self.$route.query.formStatus : "N",
					}
				});

				const promise = forms.get();

				promise.then(response => {
					self.$set(self, 'formCodes', forms.getResponseData());
				});
			},
			loadFormValues() {
				const self = this;

				let forms = new StudentDataForms({
					routeParams: { setting: 'defaulted-form-values' },
					query: {
						student_rid:			self.selectedStudent.STUDENT_RID,
						dv_form_id:				self.$route.params.formID,
						instance_id:			!!self.$route.params.instanceID ? self.$route.params.instanceID : 0,
						form_status_flg: !!self.$route.query.formStatus ? self.$route.query.formStatus : "N",
					}
				});

				const promise = forms.get();

				promise.then(response => {
					self.$set(self, 'formValues', forms.getResponseData());
					
					let data = forms.getResponseData();

					// Loop through each page
					for(let p in self.formData.PAGES) {
						let page = self.formData.PAGES[p];

						// Loop through each group
						for(let g in page.GROUPS) {
							let group = page.GROUPS[g];

							// Loop through fields
							for(let f in group.FIELDS) {
								let field = group.FIELDS[f];

								// Prevent value of multi-select field being set to a string instead of an array
								if(!!data[field.FIELD_ID]) {
									// Check if options are a checkboxes, convert to an array
									if(field.FIELD_INPUT === 'LM') {
										self.$set(field, 'DISPLAY_VALUE', data[field.FIELD_ID].split(','));
									} else {
										// Add value to field
										self.$set(field, 'DISPLAY_VALUE', data[field.FIELD_ID]);
									}

								}

								if(!data[field.FIELD_ID]) {
									// Check if the drop-down is No by default
									if(field.FIELD_TYPE === 'N/Y') {
										self.$set(field, 'DISPLAY_VALUE', 'N');
									}

									// Check if the drop-down is Yes by default
									if(field.FIELD_TYPE === 'Y/N') {
										self.$set(field, 'DISPLAY_VALUE', 'Y');
									}
								}
							}
						}
					}
					self.$set(self, 'formDataInit', JSON.parse(JSON.stringify((this.formData))));
				});
			},
			appendFile(file, fieldId){
				this.tempFiles[fieldId] = file;
				for(let i = (file.length-1);i>=0;i--){
					let itm = file[i];
					if( !!itm.success && itm.success && !!itm.response.data){
						console.log(fieldId)
						if(!Array.isArray(this.formValues[fieldId]) && this.formValues[fieldId].hasOwnProperty("DATA") && this.formValues[fieldId].DATA.hasOwnProperty('FILES')){
							this.formValues[fieldId].DATA.FILES.push({...itm.response.data});
						}
						else{
							this.formValues[fieldId].push({...itm.response.data});
						}
						
						file.splice(i, 1);
						if(itm.file.type.indexOf("htm") > -1){
							if(!Array.isArray(this.formValues[fieldId]) && this.formValues[fieldId].hasOwnProperty("DATA") && this.formValues[fieldId].DATA.hasOwnProperty('FILES')){
								this.deleteFile(
									this.formDataInit.DV_FORM_ID, 
									fieldId, 
									this.formValues[fieldId].DATA.FILES.length - 1, 
									this.formDataInit.DV_FORM_SEQ, 
									this.selectedStudent.STUDENT_RID, 
									(this.formValues[fieldId].DATA.FILES.length - 1)
								);
							}
							else{
								this.deleteFile(
									this.formDataInit.DV_FORM_ID, 
									fieldId, 
									this.formValues[fieldId].length - 1, 
									this.formDataInit.DV_FORM_SEQ, 
									this.selectedStudent.STUDENT_RID, 
									(this.formValues[fieldId].length - 1)
								);
							}
							
							alert('Error: Invalid file type, html file types are not allowed.');
							
						}
					}
				}
				this.tempFiles[fieldId] = file;
			},
			deleteFile(formId, fieldId, fileIdx, instanceId, studentRid, index){
				let File = new StudentDataForms({
					routeParams: { setting: 'field-files' },
					query: {
						student_rid:	studentRid,
						dv_form_id:		formId,
						instance_id:	instanceId,
						file_idx: fileIdx,
						field_id: fieldId,
					}
				});

				File.delete().then( () => {
					if(this.formValues[fieldId].hasOwnProperty("DATA") && this.formValues[fieldId].DATA.hasOwnProperty('FILES')){
						this.formValues[fieldId].DATA.FILES.splice(index, 1);
					}
					else{
						this.formValues[fieldId].splice(index, 1);
					}
				});
			},
			setPageTitle() {
				this.$store.commit( 'setTitle', this.$t('MAIN_CONTENT.DataForm.titleWithFormName', { title: this.formData.DV_FORM_NAME } ) );
				this.$store.commit('setUseHeader', true);
			},
			isStepActive(step) {
				return this.currentStep === step;
			},
			onClickStep(step) {
				this.currentStep = step;
			},
			getTooltip(field) {
				let tip = '';

				if(field.FIELD_INPUT !== 'HT') {
					tip = field.DISTRICT_HELP_TEXT;
				}

				return tip;
			},
			displayGroup(group) {
				let display = true;

				// Loop through all fields if group has conditional logic
				if(!!group.CONDITIONAL_FIELD_ID) {
					// Loop through pages
					for(let p in this.formData.PAGES) {
						let page = this.formData.PAGES[p];

						// Loop through groups in each page
						for(let g in page.GROUPS) {
							let groupTwo = page.GROUPS[g];

							// Loop through fields in each group
							for(let f in groupTwo.FIELDS) {
								let field = groupTwo.FIELDS[f];

								if(field.FIELD_ID === group.CONDITIONAL_FIELD_ID) {
									if( !group.CONDITIONAL_FIELD_VALUES.toString().toUpperCase().split(',').includes(field.DISPLAY_VALUE.toString().toUpperCase()) ) {
										display = false;
									}
								}
							}
						}
					}
				}

				return display;
			},
			groupToShowAfter(groups) {
				let visibleGroups = [];

				for(let g in groups) {
					let group = groups[g];

					if((+g + 1) < groups.length) {
						if(this.displayGroup(groups[+g + 1])) {
							visibleGroups.push(group.GROUP_ID);
						}
					}
				}

				return visibleGroups;
			},
			isMedWidget(field) {
				return field.WIDGET_CODE === 'MED_CONDITIONS' ||
							 field.WIDGET_CODE === 'MED_CONDITIONS_ALLG' ||
							 field.WIDGET_CODE === 'MED_CONDITIONS_NOT_ALLG' ||
							 field.WIDGET_CODE === 'STANDING_ORDERS';
			},
			submitForm() {
				const self = this;
				this.formIsSubmitted = true;

				let forms = new StudentDataForms({
					routeParams: { setting: 'form' },
					query: {
						student_rid:	self.selectedStudent.STUDENT_RID,
						dv_form_id:		self.$route.params.formID,
						instance_id:	self.$route.params.instanceID,
						form_status_flg: !!self.$route.query.formStatus ? self.$route.query.formStatus : "N",
					},
					data: self.getSavedData()
				});
				self.$set(self, 'formDataInit', JSON.parse(JSON.stringify((this.formData))));
				const promise = forms.put();
			},
			closeForm(){
				if( !!this.$route.query.referrer && this.$route.query.referrer === 'home' ){
					this.$router.push({ name: 'HomePage' })
				} else {
					this.$router.push({ name: 'StudentDataForms' });
				}	
			},
			saveProgress( submitFormOnLastPage = true) {
				const self = this;
				if(this.isReadOnly){
					return Promise.resolve();
				}

				let forms = new StudentDataForms({
					routeParams: { setting: 'form-values' },
					query: {
						student_rid:	self.selectedStudent.STUDENT_RID,
						dv_form_id:		self.$route.params.formID,
						instance_id:	self.$route.params.instanceID,
						page_id:			self.currentStep.PAGE_ID,
						form_status_flg: !!self.$route.query.formStatus ? self.$route.query.formStatus : "N",
					},
					data: self.getSavedData()
				});

				const promise = forms.put();

				promise.then(response => {
					if(!!forms.getResponseData().TYPE) {
						self.$set(self, 'message', forms.getResponseData());
						window.scroll(0,0);
						if(forms.getResponseData().TYPE == "success"){
							for(let p in self.formData.PAGES) {
								let page = self.formData.PAGES[p];

								if(submitFormOnLastPage && self.currentStep.PAGE_ID === page.PAGE_ID) {
									if(+p + 1 === self.formData.PAGES.length) {
										this.submitForm();
									}
								}
							}
						}
						
					}
				});
				self.$set(self, 'formDataInit', JSON.parse(JSON.stringify((this.formData))));
				return promise;
				
			},
			getSavedData() {
				let data = {};

				// Loop through pages
				for(let p in this.formData.PAGES) {
					let page = this.formData.PAGES[p];

					// Loop through groups
					for(let g in page.GROUPS) {
						let group = page.GROUPS[g];

						// loop through fields
						for(let f in group.FIELDS) {
							let field = group.FIELDS[f];
							if( field.FIELD_INPUT === 'WID' && this.isMedWidget(field) ){
								data[`${field.FIELD_ID}`] = field.DISPLAY_VALUE.DATA;
							} else {
								data[`${field.FIELD_ID}`] = field.DISPLAY_VALUE;
							}
						}
					}
				}

				return JSON.stringify(data);
			},
			backStep() {
				const p = this.saveProgress(false); // false for Do not submit on last page
				p.then(() => {
					for(let p in this.formData.PAGES) {
						let page = this.formData.PAGES[p];

						if(this.currentStep.PAGE_ID === page.PAGE_ID) {
							if(!!this.formData.PAGES[+p - 1]) {
								this.$set(this, 'currentStep', this.formData.PAGES[+p - 1]);
								window.scrollTo(0, 0);
							}
							break;
						}
					}
					this.$set(this, 'message', {});
				});
			},
			nextStep() {
				const p = this.saveProgress();
				p.then(() => {
					for(let p in this.formData.PAGES) {
						let page = this.formData.PAGES[p];

						if(this.currentStep.PAGE_ID === page.PAGE_ID) {
							if(!!this.formData.PAGES[+p + 1]) {
								this.$set(this, 'currentStep', this.formData.PAGES[+p + 1]);
								window.scrollTo(0, 0);
							}
							break;
						}
					}
					
					this.$set(this, 'message', {});
				});
			},


		},
		created() {
			this.loadFormData();
			this.getExternalData();
			this.loadFormCodes();
			this.$store.commit('setTitleIcon', 'clipboard');
		},
		beforeRouteLeave( to, from, next ) {
			// called when the route that renders this component is about to
			// be navigated away from.
			// has access to `this` component instance.

			const self = this;

			if ( this.isDirty && confirm( "The form has been modified. Are you sure you want to leave without saving changes?")) {
				next();
			} else if(!this.isDirty) {
				next();
			}
		},
	}
</script>

<style scoped>
	.normal-font {
		font-weight: normal;
	}
</style>
